import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Eye, EyeSlash, LockKey, Spinner } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

const LEADER_TOKEN_KEY = "ccfl_leader_token";

export default function LeaderPasswordResetConfirm() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Reset link is missing the token. Please request a new one.");
    }
  }, [token]);

  if (!token) return <Navigate to="/auth/reset-request" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/leader/password-reset/confirm", {
        token,
        new_password: pw,
      });
      // Persist new token and refresh auth
      localStorage.setItem(LEADER_TOKEN_KEY, data.token);
      await refresh();
      toast.success("Password set. You're signed in.");
      nav("/start", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not reset password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Reset Password</div>
        <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          Set a new password.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Choose at least 8 characters. You'll be signed in immediately.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-4 anim-in delay-2">
        <label className="block">
          <span className="label-kicker block mb-2">New password</span>
          <div className="relative">
            <LockKey size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-confirm-password"
              required
              minLength={8}
              autoFocus
              autoComplete="new-password"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-12 py-3.5 text-[#121815]"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="reset-confirm-toggle-pw"
              aria-label={showPw ? "Hide password" : "Show password"}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] transition-colors"
            >
              {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
            </button>
          </div>
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="reset-confirm-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60"
        >
          {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} weight="duotone" />}
          Set new password
        </button>
        <p className="text-xs text-[#8A948E] text-center pt-2">
          Link not working? <Link to="/auth/reset-request" className="text-[#1E3A2B] underline">Request a new one →</Link>
        </p>
      </form>
    </div>
  );
}
